import { Tooltip } from 'antd';
import React, { FC, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { setDirtyState } from 'data/templates';
import { useTranslation } from 'hooks/use-translation';
import { useAccountProfile } from 'queries/account';
import { queueMacrotask } from 'utils/macrotask';
import { getTimezoneAbbreviation } from 'utils/timezone';
import { getNextSendTime } from '../asu-utils';
import { useStatusUpdatesTemplatesContext } from '../context';
import {
  Container,
  EditorHeading,
  Head,
  HeaderAlert,
  ToastContainer,
} from './styles';

const TimezoneInfo = styled.label`
  margin-left: var(--spacing-02);
  display: inline-flex;
  align-items: center;
  font-size: var(--font-size-base);
`;

export const EditorHeader: FC = () => {
  const titleRef = useRef<any>(null);
  const toastContainer = useRef<HTMLDivElement | null>(null);
  const {
    templateId,
    templateName,
    setTemplateName,
    isPrivate,
    frequency,
    isPublished,
    serverPublishedTime,
  } = useStatusUpdatesTemplatesContext();
  const dispatch = useDispatch();
  const { data: account } = useAccountProfile();

  const [title, setTitle] = useState(templateName || '');

  const documentTitlePlaceholder = useTranslation('untitled');
  const untitled = useTranslation('untitled');

  const updateHeading = () => {
    if (title !== templateName) {
      dispatch(
        setDirtyState({
          id: templateId,
          _state: true,
        }),
      );
      if (!title) {
        setTitle(untitled);
      }
      setTemplateName(title || untitled);
    }
  };

  const onTitleChange = (e) => {
    setTitle(e.target.value);
  };

  useEffect(() => {
    queueMacrotask(() => {
      titleRef.current?.focus();
    });
  }, [titleRef]);

  const nextScheduledDate = getNextSendTime(
    frequency.onTime.value,
    frequency.onDay.value,
    account?.timeZone,
  );

  return (
    <>
      <Container>
        <ToastContainer ref={toastContainer} />
        <Head>
          <EditorHeading
            data-cy="status_update.template.title"
            autoFocus
            ref={titleRef}
            placeholder={documentTitlePlaceholder}
            value={title}
            onChange={onTitleChange}
            onBlur={updateHeading}
            onPressEnter={updateHeading}
            autoSize={{ minRows: 1, maxRows: 6 }}
          />
        </Head>
      </Container>
      {isPublished || isPrivate ? (
        <HeaderAlert
          message={
            <FormattedMessage
              id={
                isPublished
                  ? 'templates.status_updates.tab.config.scheduled_date'
                  : 'templates.status_updates.private'
              }
              values={{
                private: isPrivate,
                date: serverPublishedTime || nextScheduledDate,
                strong: (chunks) => (
                  <Tooltip
                    title={
                      account?.timeZone ? (
                        <FormattedMessage
                          id="timezone.info.tooltip"
                          values={{
                            timezone: account?.timeZone,
                          }}
                        />
                      ) : null
                    }
                  >
                    <strong>
                      {chunks}
                      {account?.timeZone ? (
                        <TimezoneInfo>
                          {getTimezoneAbbreviation(account?.timeZone)}
                        </TimezoneInfo>
                      ) : null}
                    </strong>
                  </Tooltip>
                ),
              }}
            />
          }
          showIcon
          type={isPrivate ? 'private' : 'info'}
        />
      ) : null}
    </>
  );
};
